import { useState } from "react";
import axios from "axios";
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextField, Typography,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import config from "../config";
import themeColors from "../theme";

export default function MarketplaceNotifications() {
    const { token } = useAuth();
    const [itemText, setItemText] = useState("");
    const [preview, setPreview] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const items = [...new Map(itemText.split(/\r?\n/).map((line) => line.trim().replace(/\s+/g, " "))
        .filter(Boolean).map((item) => [item.toLowerCase(), item])).values()];
    const valid = items.length > 0 && items.length <= 20 && items.every((item) => item.length <= 200);

    async function sendNotification() {
        if (!valid || sending) return;
        setSending(true);
        setError("");
        setResult(null);
        try {
            const { data } = await axios.post(`${config.SERVER_URL}/api/admin/marketplace-updates/notify`,
                { items }, { headers: { Authorization: `Bearer ${token}` } });
            setResult(data);
            setPreview(false);
        } catch (err) {
            setError(err.response?.data?.error || "Could not complete the notification request. You can retry; already processed recipients will be skipped.");
        } finally {
            setSending(false);
        }
    }

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" color={themeColors.teal} gutterBottom>Marketplace additions</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                After publishing new Marketplace items, list them below to announce them to verified users.
                Users who opted out of Marketplace emails are skipped.
            </Typography>
            <TextField fullWidth label="New additions" multiline minRows={3} value={itemText} disabled={sending}
                inputProps={{ maxLength: 4020 }} error={!!itemText.trim() && !valid}
                helperText="Enter one item per line (up to 20 items, 200 characters each)."
                onChange={(event) => { setItemText(event.target.value); setResult(null); setError(""); }} />
            <Button variant="contained" sx={{ mt: 2, backgroundColor: themeColors.teal }}
                disabled={!valid || sending} onClick={() => { setError(""); setPreview(true); }}>
                Preview Marketplace email
            </Button>
            {error && !preview && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {result && <Alert severity={result.failed ? "warning" : "success"} sx={{ mt: 2 }}>
                {result.sent} sent. {result.alreadyProcessed} already processed. {result.skipped} skipped. {result.failed} failed.
                {result.failed > 0 && " Review delivery logs before retrying uncertain deliveries."}
            </Alert>}
            <Dialog open={preview} onClose={() => { if (!sending) setPreview(false); }} fullWidth maxWidth="sm">
                <DialogTitle>Preview Marketplace email</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Recipients: verified users subscribed to Marketplace emails. Each recipient’s first name will be filled in automatically.
                    </Typography>
                    <Typography fontWeight="bold" mb={2}>Subject: New additions to the OnCallCME Marketplace</Typography>
                    <Typography paragraph>Hi [First Name],</Typography>
                    <Typography paragraph><strong>We’ve added new items to the OnCallCME Marketplace.</strong></Typography>
                    <Typography paragraph>Check out the latest resources and products we’ve added for Emergency Medicine clinicians.</Typography>
                    <Typography fontWeight="bold">New additions include:</Typography>
                    <ul>{items.map((item) => <li key={item.toLowerCase()}>{item}</li>)}</ul>
                    <Typography color={themeColors.teal} fontWeight="bold" mb={2}>Explore the Marketplace →</Typography>
                    <Typography paragraph>We’re continually adding new items to help you get more value from your CME time and funds.</Typography>
                    <Typography paragraph>— The OnCallCME Team</Typography>
                    <Typography variant="body2" fontStyle="italic">
                        Some Marketplace links may be affiliate links, meaning OnCallCME may earn a small commission if you make a purchase. This does not affect the price you pay.
                    </Typography>
                    <Typography variant="caption" display="block" mt={2}>An unsubscribe and email preferences link is included.</Typography>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button disabled={sending} onClick={() => setPreview(false)}>Back to editing</Button>
                    <Button variant="contained" disabled={sending} onClick={sendNotification}>
                        {sending ? "Sending…" : "Send Marketplace update"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
